import axios, { type AxiosInstance } from "axios";

import { getErrorMessage } from "./errors";

import { setupCache, buildStorage } from "axios-cache-interceptor";

export interface RespMsg {
  status: number;
  message: string;
  data: any;
  debug?: { error: string; stack: string };
}

function toFormPayload(data: any) {
  if (
    data == null ||
    typeof data === "string" ||
    data instanceof URLSearchParams ||
    data instanceof FormData
  ) {
    return data;
  }

  const params = new URLSearchParams();

  Object.entries(data).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((item) => {
        params.append(
          key,
          typeof item === "object" ? JSON.stringify(item) : String(item),
        );
      });
      return;
    }

    params.append(
      key,
      typeof value === "object" ? JSON.stringify(value) : String(value),
    );
  });

  return params;
}

// 创建带缓存功能的 Axios 实例
// const memoryStorage = buildMemoryStorage(); // 创建内存存储实例

let memoryStorage: { [key: string]: any } = {};

const customStorage = buildStorage({
  async set(key, value, currentRequest?) {
    // Save both the key (hashed URL) and value in memory

    memoryStorage[key] = {
      url: currentRequest?.baseURL! + currentRequest?.url,
      data: value,
    };
  },

  async find(key) {
    // Retrieve cached data by key
    return memoryStorage[key]?.data;
  },

  async remove(key) {
    // Remove the entry by its key (hashed URL)
    delete memoryStorage[key];
  },

  async clear() {
    // Clear all cache
    memoryStorage = {};
  },
});

const axiosInstance = setupCache(
  axios.create({
    timeout: 12000,
    baseURL: import.meta.env.VITE_API_BASE_URL,
    withCredentials: true, // Ensure credentials are sent if needed
    headers: {
      "content-type": "application/x-www-form-urlencoded",
    },
    transformRequest: [(data) => toFormPayload(data)],
  }),
  {
    cacheTakeover: false,
    storage: customStorage,
    ttl: 15 * 60 * 1000, // 15分钟缓存
    interpretHeader: false, // 忽略 Cache-Control 头
    methods: ["get"], // 只缓存 GET 请求
  },
);

const checkLoginStatus = (
  tokenLoginUrl: string,
  userInfo: any,
  cb: Function,
) => {
  if (userInfo.token == null || userInfo.token == "") {
    cb && cb(false);
    return;
  }
  axiosInstance
    .post(tokenLoginUrl, {
      user_id: userInfo.user_id,
      email: userInfo.email,
    })
    .then((res) => {
      const msg = res.data || {};

      if (msg.status == 0) {
        const data = msg.data || {};
        userInfo.setEmail(data.email);
        userInfo.setToken(data.token);
        userInfo.setUserId(data.user_id);
        userInfo.setUserName(data.user_name);
        userInfo.setRole(Number(data.role || 1));

        cb && cb(true);
      } else {
        cb && cb(false);
      }
    })
    .catch((reason) => {
      // console.log(reason);
      const message = getErrorMessage(reason.status);

      cb && cb(false, message);
    });
};

// 删除指定 URL 的缓存
function deleteCacheForUrl(url: string) {
  if (url.indexOf("http") !== 0) {
    url = axiosInstance.defaults.baseURL + url;
  }

  const key = Object.keys(memoryStorage).find(
    (k) => memoryStorage[k].url === url,
  );

  if (key) {
    delete memoryStorage[key];
  }
}

export { axiosInstance, checkLoginStatus, deleteCacheForUrl };
